import { useMemo } from "react";
import Accordion from "react-bootstrap/Accordion";
import Button from "react-bootstrap/Button";
import { useDispatch } from "react-redux";
import { resources } from "@constants/hardcoded-strings";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { AddRpServiceConfigurationAttributeLabelModal } from "@containers/modals/add-rp-service-configuration-attribute-label-modal/add-rp-service-configuration-attribute-label-modal";
import { useModalContext } from "@context/use-modal-context";
import { LabelWidthTypes } from "@enums/label-width-types";
import { ModalTypes } from "@enums/modal-types";
import { AttributeTypes } from "@iddataweb/idw-common-ui/enums";
import { AXNUIContentForm, AXNUIContentLabels, SelectOption } from "@iddataweb/idw-common-ui/types";
import { AttributeLabel, AttributeTypeFieldsMap } from "@idw-types/data/reference-data/generic";
import { FormattedRpServiceConfigurationAttribute } from "@idw-types/data/rp-service-configuration";
import { addObjectsToArrayInCommonReferenceData } from "@redux/actions/common-reference-data-actions";
import { getAttributeFieldPlaceholder } from "@utils/attribute-input-util";
import { WorkflowDetailsAttributePlaceholder } from "./workflow-details-attribute-placeholder/workflow-details-attribute-placeholder";
import styles from "./workflow-details-attribute-container.less";

type WorkflowDetailsAttributeContainerProps = {
    isDisabled: boolean;
    axnuiContentLabels: AXNUIContentLabels;
    axnuiContentForm: AXNUIContentForm;
    attribute: FormattedRpServiceConfigurationAttribute;
    axnAttributeTypeFieldMap: AttributeTypeFieldsMap;
    attributeLabels: Array<AttributeLabel>;
    handleAttributeLabelChange: (value: string, attributeTypeName: AttributeTypes) => void;
    validationErrors: React.ReactNode;
    setAttributeForm: (attributeTypeName: AttributeTypes, key: keyof AXNUIContentForm, value: string) => void;
}

export const WorkflowDetailsAttributeContainer = (props: WorkflowDetailsAttributeContainerProps) => {
    const { isDisabled, axnuiContentLabels, attribute, attributeLabels, axnAttributeTypeFieldMap, axnuiContentForm, validationErrors, handleAttributeLabelChange, setAttributeForm } = props;
    const dispatch = useDispatch();
    const modalContext = useModalContext();

    const handleAttributeFieldChange = (attributeTypeName: AttributeTypes, key: keyof AXNUIContentForm, newValue: string) => {
        setAttributeForm(attributeTypeName, key, newValue);
    }
    const onChangeAttributeLabel = (newValue: SelectOption) => {
        handleAttributeLabelChange(newValue.label, attribute.attributeType.name);
    };

    const onAttributeLabelCreate = (newAttributeLabel: AttributeLabel) => {
        dispatch(addObjectsToArrayInCommonReferenceData([newAttributeLabel], "axnAttributeLabels"));
        handleAttributeLabelChange(newAttributeLabel.label, attribute.attributeType.name);
    };

    const onClickAddAttributeLabel = () => {
        const renderAddRpServiceConfigurationAttributeLabelModal = (onModalClose: () => void) => {
            return (
                <AddRpServiceConfigurationAttributeLabelModal
                    attributeType={attribute.attributeType}
                    attributeLabels={attributeLabels}
                    onAttributeLabelCreate={onAttributeLabelCreate}
                    onModalClose={onModalClose}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.AddAttributeLabel,
            renderFunction: renderAddRpServiceConfigurationAttributeLabelModal
        });
    };
    const isExpandedByDefault = true;

    const labelForTheming = useMemo(() => {
        return axnuiContentLabels.attributes[attribute.attributeType.name];
    }, [axnuiContentLabels.attributes[attribute.attributeType.name]]);

    const attributeLabelOptions: Array<SelectOption> = useMemo(() => {
        // if we have custom content property label and it doesn't exist in our labels list
        // we need to create a fake dropdown option and add it to the normal list
        if (labelForTheming) {
            const existingAttributeLabel = attribute.attributeLabels.find(attrL => attrL.label === labelForTheming);
            if (!existingAttributeLabel) {
                return [
                    ...attribute.attributeLabels.map(al => ({ label: al.label, value: al.id })),
                    { label: labelForTheming, value: null }
                ];
            }
        }

        // otherwise we just return the normal list
        return attribute.attributeLabels.map(al => ({ label: al.label, value: al.id }));
    }, [
        attribute.attributeLabels,
        axnuiContentLabels.attributes[attribute.attributeType.name]
    ]);

    const selectedOption: SelectOption = useMemo(() => {
        if (labelForTheming) {
            return attributeLabelOptions.find(al => al.label === labelForTheming);
        } else if (attribute.attributeLabel) {
            return attributeLabelOptions.find(al => al.value === attribute.attributeLabel.id);
        }

        return null;
    }, [labelForTheming, attribute.attributeLabel, attributeLabelOptions]);


    const attributeFields = useMemo(() => {
        return getAttributeFieldPlaceholder(attribute.attributeType?.name, attribute.attributeType.id, axnuiContentForm, axnAttributeTypeFieldMap);

    }, [attribute.attributeType, axnuiContentForm]);

    return (
        <div className={styles.workflowDetailsAttributeContainer}>
            <div className={styles.headerContent}>
                <span className={styles.attributeLabelInfo}>
                    {attribute.name}
                </span>
                <SelectFormGroup
                    controlId={`attribute-${attribute.attributeType?.id}-label-select`}
                    automationId={`attribute-${attribute.attributeType?.id}-label-select`}
                    className={styles.selectFormGroup}
                    ariaLabel={resources.common.inputs.attributeLabel.label}
                    label={resources.common.inputs.attributeLabel.label}
                    disabled={isDisabled}
                    isClearable={false}
                    isRequired={true}
                    isSearchable={false}
                    labelWidth={LabelWidthTypes.EXTRA_SMALL}
                    onChange={onChangeAttributeLabel}
                    options={attributeLabelOptions}
                    selectedOption={selectedOption}
                />


                {!isDisabled && (
                    <Button id={`attribute-${attribute.attributeType?.id}-add-attribute-label`} onClick={onClickAddAttributeLabel} className={styles.addAttributeLabelButton} variant={"primary"}>
                        {resources.pages.verificationServiceDetails.tabs.attributes.buttons.addLabel.label}
                    </Button>
                )}

                {validationErrors}
            </div>

            {attributeFields.length > 0 &&
                <div className={styles.placeholderContainer}>
                    <Accordion defaultActiveKey={isExpandedByDefault ? "0" : null}>
                        <Accordion.Item eventKey="0" >
                            <Accordion.Header className={styles.placeholderHeader}>Placeholders</Accordion.Header>
                            <Accordion.Body>
                                {attributeFields.map((attrField, index) =>
                                    <WorkflowDetailsAttributePlaceholder
                                        key={`workflow-details-attribute-placeholder-${index}`}
                                        isDisabled={isDisabled}
                                        attribute={attribute}
                                        attrField={attrField}
                                        handleAttributeFieldChange={handleAttributeFieldChange}
                                    />
                                )}
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                </div>
            }
        </div>
    );
}